import { Button } from '@alison/ui';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode, useState } from 'react';
import type { NextAction } from './workflows.types.js';

const MENU_WIDTH = 380;
const ITEM_MIN_HEIGHT = 45;

interface NextActionPickerProps {
  actions: NextAction[];
  /** The lead action, as resolved by `primaryAction`. */
  chosen: NextAction | undefined;
  selectedCount: number;
  /** The `{count}` the lead CTA names, when it is not the selection size. */
  ctaCount?: number;
  onChoose: (action: NextAction) => void;
  onRun: (action: NextAction) => void;
}

function blockedReason(action: NextAction, selectedCount: number): string | null {
  if (!action.enabled) return action.reason ?? 'Not available for this run.';
  if (action.maxSelection !== null && selectedCount > action.maxSelection)
    return `Select at most ${action.maxSelection} item${action.maxSelection === 1 ? '' : 's'} to continue.`;
  if (!action.requiresSelection || selectedCount >= action.minSelection) return null;
  const missing = action.minSelection - selectedCount;
  return `Select ${missing} more item${missing === 1 ? '' : 's'} to continue.`;
}

/** The user's menu pick, else the goal path's next step, else the first offered action. */
export function primaryAction(
  actions: NextAction[],
  chosenType: string | null,
  preferredActionType: string | null,
): NextAction | undefined {
  return (
    actions.find((action) => action.actionType === chosenType) ??
    actions.find((action) => action.actionType === preferredActionType) ??
    actions[0]
  );
}

export function nextActionLabel(action: NextAction, selectedCount: number): string {
  const template = selectedCount === 1 && action.ctaOne ? action.ctaOne : action.cta;
  if (!template) return `Run ${action.label}`;
  return template.replaceAll('{count}', String(selectedCount));
}

export function NextActionPicker({
  actions,
  chosen,
  selectedCount,
  ctaCount = selectedCount,
  onChoose,
  onRun,
}: NextActionPickerProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const others = actions.filter((action) => action.actionType !== chosen?.actionType);

  const withReason = (action: NextAction, button: ReactNode): ReactNode => {
    const blocked = blockedReason(action, selectedCount);
    return blocked === null ? (
      button
    ) : (
      <Tooltip title={blocked}>
        <span>{button}</span>
      </Tooltip>
    );
  };

  return (
    <>
      {others.length === 0 ? null : (
        <>
          <Button
            variant="outlined"
            onClick={(event) => setAnchor(event.currentTarget)}
            endIcon={<ExpandMoreOutlined sx={{ fontSize: 16 }} />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            More workflows
          </Button>
          <Menu
            anchorEl={anchor}
            open={anchor !== null}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: -1, width: MENU_WIDTH, borderRadius: 1 } } }}
          >
            <Stack sx={{ px: 3, pt: 1, pb: 1 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                CHAIN WORKFLOWS
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                You can add others later
              </Typography>
            </Stack>

            {actions.map((action, index) => {
              return (
                <Fragment key={action.actionType}>
                  {index === 0 ? null : <Divider sx={{ mx: 1 }} />}
                  <MenuItem
                    selected={action.actionType === chosen?.actionType}
                    onClick={() => {
                      onChoose(action);
                      setAnchor(null);
                    }}
                    sx={(theme) => ({
                      px: 2.5,
                      py: 1,
                      m: 0.5,
                      display: 'block',
                      whiteSpace: 'normal',
                      minHeight: ITEM_MIN_HEIGHT,
                      [theme.breakpoints.up('sm')]: { minHeight: ITEM_MIN_HEIGHT },
                    })}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{action.label}</Typography>
                    {action.description === '' ? null : (
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {action.description}
                      </Typography>
                    )}
                  </MenuItem>
                </Fragment>
              );
            })}
          </Menu>
        </>
      )}

      {chosen === undefined
        ? null
        : withReason(
            chosen,
            <Button
              variant="gradient"
              disabled={blockedReason(chosen, selectedCount) !== null}
              onClick={() => onRun(chosen)}
              startIcon={<ArrowForwardOutlined sx={{ fontSize: 16 }} />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {nextActionLabel(chosen, ctaCount)}
            </Button>,
          )}
    </>
  );
}
